import React, {useCallback, useMemo, useState} from 'react';

import {addAndUpdateSteps} from 'analysis-tool/AnalysisActions';
import {getArchivedTooltipMessage} from 'analysis-tool/AnalysisToolMessages';
import {isAnalysisOutputStepType, isNonRefreshableStepType} from 'analysis-tool/AnalysisTypes';
import getDownstreamSteps from 'analysis-tool/data/getDownstreamSteps';
import isAnalysisRunning from 'analysis-tool/isAnalysisRunning';
import isArchived from 'archive/isArchived';
import {PipelineGraphQL, PipelineStep} from 'pipelines/PipelineTypes';
import {getInputStepDatas} from 'pipelines/StepConnectionUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {readableTime} from 'util/DateUtil';

interface RefreshDataButtonDisabledReason {
  isDisabled: boolean;
  tooltipText: string;
}

interface UseRefreshDataButtonStateResult extends RefreshDataButtonDisabledReason {
  buttonText: string;
  handleClick: () => Promise<void>;
}

/**
 * Get all data source steps in the given analysis.
 *
 * These are those steps in the analysis whose step types do not accept any input datas.
 */
function getAllSourceSteps(analysis: PipelineGraphQL): Array<PipelineStep> {
  return analysis.steps.filter((step) => getInputStepDatas(step.stepType).length === 0);
}

/**
 * Get all data source steps in the given analysis that are refreshable.
 */
function getRefreshableDataSourceSteps(analysis: PipelineGraphQL): Array<PipelineStep> {
  return getAllSourceSteps(analysis).filter((step) => !isNonRefreshableStepType(step.stepType));
}

/**
 * Checks if any source steps in the given analysis have output descendants.
 *
 * @param analysis The analysis to check for output steps
 * @returns True if any source steps in the given analysis lead to output steps, false otherwise
 */
function hasAnySourceStepWithOutputDescendants(analysis: PipelineGraphQL): boolean {
  return getAllSourceSteps(analysis).some((sourceStep) => {
    const downstreamSteps = getDownstreamSteps(analysis, sourceStep.id);
    for (const step of downstreamSteps) {
      if (isAnalysisOutputStepType(step.definition.stepType)) {
        return true;
      }
    }
    return false;
  });
}

/**
 * Rerun the given steps within the given analysis.
 * @param analysisId The ID of the analysis where the steps to rerun are
 * @param steps The steps to rerun
 * @returns The result of the rerun steps action
 */
function rerunSteps(analysisId: string, steps: Array<PipelineStep>): ReturnType<typeof addAndUpdateSteps> {
  return addAndUpdateSteps(analysisId, {
    stepsToAdd: [],
    stepsToUpdate: steps.map((step) => ({
      stepId: step.id,
      rerunAfterUpdate: true,
    })),
  });
}

/**
 * Timestamp for when refreshable data sources were last refreshed (*collectively* as an analysis).
 *
 * Uses the *oldest* `modifiedAt` among refreshable source steps: the analysis is only as current
 * as the stalest source, even if other sources were updated more recently.
 *
 * @param analysis The analysis to read execution timestamps from
 * @param refreshableDataSourceSteps Refreshable data source steps to include
 * @returns That collective refresh time as an ISO string, or null if none have timestamps
 */
function getLastRefreshTimestamp(
  analysis: PipelineGraphQL,
  refreshableDataSourceSteps: Array<PipelineStep>
): string | null {
  const refreshableIds = new Set(refreshableDataSourceSteps.map((step) => step.id));
  const timestamps = analysis.execution.steps
    .filter((step) => refreshableIds.has(step.id))
    .map((step) => step.modifiedAt)
    .filter((timestamp): timestamp is string => timestamp != null);
  if (timestamps.length === 0) {
    return null;
  }
  return timestamps.reduce((earliestTimestamp, timestamp) =>
    new Date(timestamp).getTime() < new Date(earliestTimestamp).getTime() ? timestamp : earliestTimestamp
  );
}

/**
 * Appends a line to the given tooltip text indicating the last refresh timestamp, if applicable.
 *
 * @param baseTooltip The base tooltip text
 * @param isDisabled Whether the refresh button is disabled
 * @param lastRefreshTimestamp The last refresh timestamp as an ISO string, or null
 * @returns The tooltip text with the last refresh timestamp line, if applicable
 */
function withOptionalLastRefreshedLine(
  baseTooltip: string,
  isDisabled: boolean,
  lastRefreshTimestamp: string | null
): string {
  if (isDisabled || lastRefreshTimestamp == null) {
    return baseTooltip;
  }
  return `${baseTooltip}\nLast refreshed: ${readableTime(lastRefreshTimestamp)}`;
}

/**
 * Check whether the refresh button should be disabled for the given analysis, and if so, the reason.
 *
 * @param analysis The analysis to check
 * @returns RefreshDataButtonDisabledReason, which contains:
 *   - isDisabled: boolean indicating whether the refresh button should be disabled
 *   - tooltipText: user-facing string to show in the button's tooltip
 */
function getRefreshButtonDisabledReason(analysis: PipelineGraphQL): RefreshDataButtonDisabledReason {
  // Archiving is handled prior to permissions check because an archived analysis will not be writable.
  // This order of checks ensures a more specific error in the case of an archived analysis.
  if (isArchived(analysis)) {
    return {isDisabled: true, tooltipText: getArchivedTooltipMessage(analysis)};
  }

  // As with other similar buttons in the analysis header, this message should not actually be shown to the
  // user, because options to modify the analysis should be hidden if the analysis is read-only. This check is
  // still included for completeness.
  if (!analysis.permissions.writable) {
    return {
      isDisabled: true,
      tooltipText: 'You do not have permissions to modify this analysis.',
    };
  }

  if (getRefreshableDataSourceSteps(analysis).length === 0) {
    return {isDisabled: true, tooltipText: 'No data source steps to refresh.'};
  }

  if (isAnalysisRunning(analysis)) {
    return {isDisabled: true, tooltipText: 'Cannot refresh while steps are running.'};
  }

  if (hasAnySourceStepWithOutputDescendants(analysis)) {
    return {
      isDisabled: true,
      tooltipText: 'Cannot refresh because one or more source steps lead to outputs.',
    };
  }

  return {
    isDisabled: false,
    tooltipText: 'Refresh all data sources. Note: this refresh will use your permissions.',
  };
}

function useRefreshDataButtonState(analysis: PipelineGraphQL): UseRefreshDataButtonStateResult {
  const [isRefreshRequestRunning, setIsRefreshRequestRunning] = useState(false);
  const buttonDisabledState = useMemo(() => getRefreshButtonDisabledReason(analysis), [analysis]);
  const lastRefreshTimestamp = useMemo(
    () => getLastRefreshTimestamp(analysis, getRefreshableDataSourceSteps(analysis)),
    [analysis]
  );

  const handleClick = useCallback(async (): Promise<void> => {
    if (isRefreshRequestRunning || buttonDisabledState.isDisabled) {
      return;
    }

    setIsRefreshRequestRunning(true);
    const refreshableDataSourceSteps = getRefreshableDataSourceSteps(analysis);
    try {
      await rerunSteps(analysis.id, refreshableDataSourceSteps);
    } finally {
      setIsRefreshRequestRunning(false);
    }
  }, [analysis, buttonDisabledState.isDisabled, isRefreshRequestRunning]);

  if (isRefreshRequestRunning) {
    return {
      isDisabled: true,
      tooltipText: 'Data refresh in progress',
      buttonText: 'Refreshing data...',
      handleClick,
    };
  }

  return {
    ...buttonDisabledState,
    tooltipText: withOptionalLastRefreshedLine(
      buttonDisabledState.tooltipText,
      buttonDisabledState.isDisabled,
      lastRefreshTimestamp
    ),
    buttonText: 'Refresh data',
    handleClick,
  };
}

export interface RefreshDataButtonProps {
  analysis: PipelineGraphQL;
}

export default function RefreshDataButton({analysis}: RefreshDataButtonProps): JSX.Element {
  const {buttonText, handleClick, isDisabled, tooltipText} = useRefreshDataButtonState(analysis);

  return (
    // Deprecated ui-library tooltip used intentionally to align with other buttons in the same header
    <Tooltip text={tooltipText}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        dataTestAttrs={{component: 'RefreshDataButton', element: 'root'}}
        icon="refresh"
        size={ButtonSize.SMALL}
        text={buttonText}
        disabled={isDisabled}
        onClick={handleClick}
      />
    </Tooltip>
  );
}
